// The library's statistics for the home page and the Corpus page (Oct 2026).
// The numbers are a build artifact — public/library-stats.json, written by
// scripts/build-library-stats.ts from every collection — so a page fetches one
// small file and analyses nothing. A file that can't be fetched or doesn't
// read as statistics gives null: the home page simply draws no section and
// never depends on it, the Corpus page says the numbers are unavailable. A
// failed fetch is not remembered, so the next visit tries again.

import { useEffect, useState } from 'react';
import type { RankedCount } from '@/music/corpus';
import type {
  LibraryStats,
  LibraryStatsGroup,
  LibraryStatsKey,
  LibraryStatsLeagueRow,
  LibraryStatsProgression,
  LibraryStatsTune,
} from '@/music/libraryStats';

type Raw = Record<string, unknown>;
const isObject = (value: unknown): value is Raw =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

function indexList(value: unknown, size: number): number[] | null {
  if (!Array.isArray(value)) return null;
  return value.every((i) => Number.isInteger(i) && i >= 0 && i < size) ? (value as number[]) : null;
}

function group(value: unknown, size: number): LibraryStatsGroup | null {
  if (!isObject(value) || typeof value.label !== 'string') return null;
  const tunes = indexList(value.tunes, size);
  return tunes ? { label: value.label, tunes } : null;
}

function groups<T>(value: unknown, read: (item: unknown) => T | null): T[] | null {
  if (!Array.isArray(value)) return null;
  const out: T[] = [];
  for (const item of value) {
    const parsed = read(item);
    if (!parsed) return null;
    out.push(parsed);
  }
  return out;
}

const count = (item: unknown): RankedCount | null =>
  isObject(item) && typeof item.label === 'string' && isFiniteNumber(item.count)
    ? { label: item.label, count: item.count }
    : null;

function counts<K extends string>(
  value: unknown,
  keys: readonly K[],
): Record<K, RankedCount[]> | null {
  if (!isObject(value)) return null;
  const out = {} as Record<K, RankedCount[]>;
  for (const key of keys) {
    const list = groups(value[key], count);
    if (!list) return null;
    out[key] = list;
  }
  return out;
}

function leagues<K extends string>(
  value: unknown,
  keys: readonly K[],
  size: number,
): Record<K, LibraryStatsLeagueRow[]> | null {
  if (!isObject(value)) return null;
  const out = {} as Record<K, LibraryStatsLeagueRow[]>;
  for (const key of keys) {
    const list = groups<LibraryStatsLeagueRow>(value[key], (item) =>
      isObject(item) &&
      Number.isInteger(item.tune) &&
      (item.tune as number) >= 0 &&
      (item.tune as number) < size &&
      isFiniteNumber(item.value)
        ? { tune: item.tune as number, value: item.value }
        : null,
    );
    if (!list) return null;
    out[key] = list;
  }
  return out;
}

/** One neighbour list per tune: pairs of a tune index and a percentage. */
function neighbourLists(value: unknown, size: number): number[][] | null {
  if (!Array.isArray(value) || value.length !== size) return null;
  for (const list of value) {
    if (!Array.isArray(list) || list.length % 2 !== 0) return null;
    for (let i = 0; i < list.length; i += 2) {
      const [tune, pct] = [list[i], list[i + 1]];
      if (!Number.isInteger(tune) || tune < 0 || tune >= size) return null;
      if (!Number.isInteger(pct) || pct < 0 || pct > 100) return null;
    }
  }
  return value as number[][];
}

const TOTALS = [
  'tunes',
  'composers',
  'chords',
  'analysed',
  'meanBars',
  'meanChordsPerBar',
  'iiVShare',
  'iiVITuneShare',
  'minorShare',
] as const;
const VOCABULARY = [
  'families',
  'qualities',
  'degrees',
  'bigrams',
  'trigrams',
  'timeSigs',
  'durations',
] as const;
const LEAGUES = [
  'adventurous',
  'predictable',
  'densest',
  'longest',
  'iiV',
  'hardest',
  'easiest',
] as const;

/**
 * The file as a page may trust it, or null. Strict rather than
 * forgiving: the file is generated, so anything malformed means a build went
 * wrong, and drawing half of it would be worse than drawing none.
 */
export function parseLibraryStats(raw: unknown): LibraryStats | null {
  if (!isObject(raw) || raw.version !== 1 || !Array.isArray(raw.tunes)) return null;
  const tunes: LibraryStatsTune[] = [];
  for (const tune of raw.tunes) {
    if (!isObject(tune) || typeof tune.t !== 'string' || !tune.t) return null;
    if (tune.c !== undefined && typeof tune.c !== 'string') return null;
    if (tune.rb !== undefined && tune.rb !== 1) return null;
    if (tune.m !== undefined && tune.m !== 1) return null;
    const { p } = tune;
    if (p !== undefined && (!Number.isInteger(p) || (p as number) < 0 || (p as number) > 99)) {
      return null;
    }
    tunes.push({
      t: tune.t,
      ...(tune.c ? { c: tune.c } : {}),
      ...(tune.rb ? { rb: 1 as const } : {}),
      ...(tune.m ? { m: 1 as const } : {}),
      ...(p ? { p: p as number } : {}),
    });
  }
  const size = tunes.length;

  const rawTotals = raw.totals;
  if (!isObject(rawTotals) || !TOTALS.every((key) => isFiniteNumber(rawTotals[key]))) return null;
  const totals = Object.fromEntries(TOTALS.map((key) => [key, rawTotals[key]])) as Record<
    (typeof TOTALS)[number],
    number
  >;

  const keys = groups<LibraryStatsKey>(raw.keys, (item) => {
    const base = group(item, size);
    if (!base || !isObject(item)) return null;
    const { pc, minor } = item;
    if (!Number.isInteger(pc) || (pc as number) < 0 || (pc as number) > 11) return null;
    if (typeof minor !== 'boolean') return null;
    return { ...base, pc: pc as number, minor };
  });
  const feels = groups(raw.feels, (item) => group(item, size));
  const forms = groups(raw.forms, (item) => group(item, size));
  const composers = groups(raw.composers, (item) => group(item, size));
  const progressions = groups<LibraryStatsProgression>(raw.progressions, (item) => {
    const base = group(item, size);
    if (!base || !isObject(item) || !isFiniteNumber(item.count)) return null;
    return { ...base, count: item.count };
  });
  const rootMotion = groups(raw.rootMotion, count);
  const neighbours = neighbourLists(raw.neighbours, size);
  const vocabulary = counts(raw.vocabulary, VOCABULARY);
  const tables = leagues(raw.leagues, LEAGUES, size);
  if (
    !keys ||
    !feels ||
    !forms ||
    !composers ||
    !progressions ||
    !rootMotion ||
    !vocabulary ||
    !neighbours ||
    !tables
  ) {
    return null;
  }

  return {
    version: 1,
    tunes,
    totals,
    keys,
    feels,
    forms,
    progressions,
    composers,
    rootMotion,
    vocabulary,
    neighbours,
    leagues: tables,
  };
}

let pending: Promise<LibraryStats | null> | null = null;
let loaded: LibraryStats | null = null;

/** The statistics, fetched once per session; null when unavailable. */
export function libraryStats(): Promise<LibraryStats | null> {
  if (loaded) return Promise.resolve(loaded);
  pending ??= (async () => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}library-stats.json`);
      if (!res.ok) return null;
      loaded = parseLibraryStats(await res.json());
      return loaded;
    } catch {
      return null;
    } finally {
      // Settled either way: a success is held in `loaded`, a failure retries.
      pending = null;
    }
  })();
  return pending;
}

/** For tests: forget the session's copy. */
export function resetLibraryStats(): void {
  pending = null;
  loaded = null;
}

export interface LibraryStatsState {
  stats: LibraryStats | null;
  /** The file could not be fetched or read; a later mount tries again. */
  failed: boolean;
}

export function useLibraryStatsState(): LibraryStatsState {
  const [state, setState] = useState<LibraryStatsState>(() => ({ stats: loaded, failed: false }));
  useEffect(() => {
    if (state.stats) return;
    let live = true;
    void libraryStats().then((stats) => {
      if (live) setState({ stats, failed: !stats });
    });
    return () => {
      live = false;
    };
  }, [state.stats]);
  return state;
}

export function useLibraryStats(): LibraryStats | null {
  return useLibraryStatsState().stats;
}
